import { useState, useEffect } from "react";
import "./tradeVerifiedLinkTable.css";
import CustomSwitch from "../../../sharedPages/CommonComponents/CustomSwitch/CustomSwitch.jsx";
import GenericTable from "../../../sharedPages/CommonComponents/Table/index.jsx";
import useApi from "../../../../helpers/apiHelper/requestHelper";
import { useDispatch, useSelector } from "react-redux";
import { toastify } from "../../../../helpers/toast/toastify.js";
import { Link, useNavigate } from "react-router-dom";
import copyIcon from "../../../../assets/images/Copy.svg";
import externalLinkIcon from "../../../../assets/images/externalLinkIcon.svg";
import dropdownAngle from "../../../../assets/images/dropdownAngle.svg";
import {
  ArrowDropDownIcon,
  DatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Tooltip } from "@mui/material";
import dayjs from "dayjs";
import VerifiedTradesTable from "../../../sharedPages/CommonComponents/Table/TradesTable.jsx";
import ExactDateSelector from "../../../sharedPages/CommonComponents/ExactDateSelector/ExactDateSelector.jsx";

// ── Helpers for VerifiedTradesTable columns ──────────────────────────────────
function formatBackendDate(isoString) {
  const date = new Date(isoString);
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const year = date.getUTCFullYear();
  return `${day}-${month}-${year}`;
}

function formatLocalTime(isoString) {
  const date = new Date(isoString);
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  const offsetMinutes = -date.getTimezoneOffset();
  const offsetHours = offsetMinutes / 60;
  const sign = offsetHours >= 0 ? "+" : "-";
  return `${hours}:${minutes}:${seconds} UTC${sign}${Math.abs(offsetHours)}`;
}

function resolveMarketLabel(trade) {
  const orders = trade?.statsSnapshot?.orders || [];
  const candidates = [
    trade?.statsSnapshot?.blockchainData?.marketType,
    trade?.statsSnapshot?.marketType,
    trade?.statsSnapshot?.tradeMarket,
    trade?.statsSnapshot?.category,
    trade?.marketType,
    ...orders.flatMap((order) => [
      order?.marketType,
      order?.category,
      order?.type,
      order?.isSpot === true || order?.spot === true ? "spot" : null,
    ]),
  ];
  for (const candidate of candidates) {
    const normalized = String(candidate || "").toLowerCase();
    if (normalized.includes("spot")) return "Spot";
    if (normalized.includes("future")) return "Futures";
  }
  // Legacy verified trades had no marketType and were futures-only.
  return "Futures";
}

function normalizeExchangeName(raw) {
  const value = String(raw || "").trim();
  if (!value) return null;
  const withoutMarket = value.replace(/\s*(futures?|spot)\s*$/i, "").trim();
  const base = withoutMarket || value;
  const lower = base.toLowerCase();
  if (lower === "binance") return "Binance";
  if (lower === "bybit" || lower === "by bit") return "Bybit";
  return base;
}

function getExchangeName(trade) {
  const rawExchange =
    trade?.statsSnapshot?.blockchainData?.exchange ||
    trade?.statsSnapshot?.exchange ||
    trade?.exchange;
  const exchange = normalizeExchangeName(rawExchange);
  if (!exchange) return "-";
  return `${exchange} ${resolveMarketLabel(trade)}`;
}

// ── Column definitions for VerifiedTradesTable (works on raw API trade objects)
const tradesTableColumns = [
  {
    label: "Date",
    width: "125px",
    render: (trade) => formatBackendDate(trade.createdAt),
    expandable: true,
    expandRender: (_, trade) => <span>{formatBackendDate(trade.createdAt)}</span>,
  },
  {
    label: "Asset",
    width: "155px",
    render: (trade, { toggle, isOpen, orders }) => {
      const hasMultiple = orders.length > 1;
      const hasSingle = orders.length === 1;
      const assetLabel = hasMultiple
        ? "Multiple Trades"
        : hasSingle
          ? orders[0]?.coinName?.includes("/")
            ? orders[0]?.coinName
            : orders[0]?.coinName?.replace(/(USDT|BUSD|USDC)$/, "/$1")
        : trade.type;
      return (
        <div
          className="assertColumn"
          onClick={() => hasMultiple && toggle(trade._id)}
        >
          <button
            className="tableDropdownButton"
            style={
              hasMultiple ? { marginBottom: isOpen(trade._id) ? 0 : 5 } : undefined
            }
          >
            <span className="selectedOption">{assetLabel}</span>
            <span
              className={`dropdownArrow ${isOpen(trade._id) ? "rotate" : ""}`}
              style={{ opacity: 1 }}
            >
              {hasMultiple && <img alt="dropdownAngle" src={dropdownAngle} />}
            </span>
          </button>
          {!isOpen(trade._id) && hasMultiple && (
            <span className="clickForSelectText">CLICK TO SEE TRADES</span>
          )}
        </div>
      );
    },
    expandable: true,
    expandRender: (order) => <span>{order?.coinName?.includes("/")
      ? order?.coinName
      : order?.coinName?.replace(/(USDT|BUSD|USDC)$/, "/$1")}</span>,
  },
  {
    label: "Exchange",
    width: "170px",
    render: (trade) => getExchangeName(trade),
    expandable: true,
    expandRender: (_, trade) => <span>{getExchangeName(trade)}</span>,
  },
  {
    label: "Time",
    width: "150px",
    render: (trade) => formatLocalTime(trade.createdAt),
    expandable: true,
    expandRender: (_, trade) => <span>{formatLocalTime(trade.createdAt)}</span>,
  },
  {
    label: "Blockchain Transaction ID",
    render: (trade) => (
      <p className="blockchainTransactionID text-truncate">
        {trade.blockChainId}
      </p>
    ),
    expandable: true,
    expandRender: (_, trade) => <span className="blockchainTransactionID text-truncate">{trade.blockChainId}</span>,
  },
  {
    label: "",
    render: (trade) => (
      <div className="VerifiedLinkIcons">
        <CopyBtn text={trade.blockChainId} />
        <LinkBtn href={trade.blockChainLink} />
      </div>
    ),
    expandable: true,
    expandRender: (_, trade) => (
      <div className="VerifiedLinkIcons">
        <CopyBtn text={trade.blockChainId} />
        <LinkBtn href={trade.blockChainLink} />
      </div>
    ),
  },
];
// ── Copy & Link buttons with tooltip + click animation ──────────────────────
const CopyBtn = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    toastify("Copied");
    setCopied(true);
    setTimeout(() => setCopied(false), 800);
  };
  return (
    <Tooltip title="Copy transaction ID" placement="top" arrow>
      <span style={{ display: "inline-flex", cursor: "pointer" }} onClick={handleCopy}>
        <img
          src={copyIcon}
          alt="copyIcon"
          className={`copyIcon${copied ? " icon-copied" : ""}`}
          width={15}
          height={15}
        />
      </span>
    </Tooltip>
  );
};

const LinkBtn = ({ href }) => {
  const [clicked, setClicked] = useState(false);
  const handleClick = () => {
    setClicked(true);
    setTimeout(() => setClicked(false), 600);
  };
  return (
    <Tooltip title="View" placement="top" arrow>
      <span style={{ display: "inline-flex" }}>
        <a href={href} target="_blank" rel="noopener noreferrer" onClick={handleClick}>
          <img
            src={externalLinkIcon}
            alt="externalLinkIcon"
            className={`externalLinkIcon${clicked ? " icon-clicked" : ""}`}
            width={15}
            height={15}
          />
        </a>
      </span>
    </Tooltip>
  );
};

// NEW: sort raw trade objects by createdAt
function sortByDateTime(arr) {
  return arr.slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}

const itemsPerPage = 10;

const TradeVerifiedLinkTable = ({
  refetchTable,
  setRefetchTable,
  isNewVipAdded,
}) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [isShowContent, setIsShowContent] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredTableData, setFilteredTableData] = useState([]);
  const api = useApi();
  const navigate = useNavigate();
  const user = useSelector((state) => state?.user.user);
  const viewOnBlockchainParams = (
    user?.leaderCommunity?.name ? user.leaderCommunity.name : user?.tradeName
  )
    ?.split(" ")
    ?.join("_");

  const [monthlyReportUpdated, setMonthlyReportUpdated] = useState(false);

  // Helper function to check if two dates are the same day
  const isSameDate = (date1, date2) => {
    if (!date1 || !date2) return false;

    const d1 = dayjs(date1);
    const d2 = dayjs(date2);

    return d1.format("YYYY-MM-DD") === d2.format("YYYY-MM-DD");
  };
  function formatTimeWithUTCOffset(isoString) {
    const date = new Date(isoString);

    // Get local time components
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    // Get time zone offset in minutes and convert to hours
    const timezoneOffsetMinutes = -date.getTimezoneOffset(); // reverse the sign
    const timezoneOffsetHours = timezoneOffsetMinutes / 60;
    const sign = timezoneOffsetHours >= 0 ? "+" : "-";

    return `${hours}:${minutes}:${seconds} UTC${sign}${Math.abs(
      timezoneOffsetHours
    )}`;
  }
  const fetchUserRecords = async () => {
    const response = await api("get", "blockchain/user-records");
    if (response?.data) {
      console.log(response.data,"this is response.data from the fetchUserRecords");
      // NEW: store raw trade objects — VerifiedTradesTable handles rendering via column render fns
      response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setTableData(response.data);
    }
  };

  useEffect(() => {
    fetchUserRecords();
  }, [user,monthlyReportUpdated]);

  useEffect(() => {
    if (refetchTable) {
      fetchUserRecords();
      if (setRefetchTable) setRefetchTable(false);
    }
  }, [refetchTable]);

  // Filter logic with date filtering
  useEffect(() => {
    const filterData = (data, selectedDate) => {
      let filtered = data;

      // Filter by exact date — now uses item.createdAt (raw trade object)
      if (selectedDate) {
        filtered = filtered.filter((item) => {
          /* OLD: used pre-mapped item.exactDate — return isSameDate(item.exactDate, selectedDate.toDate()); */
          return isSameDate(item.createdAt, selectedDate.toDate());
        });
      }

      return filtered;
    };

    setFilteredTableData(filterData(tableData, selectedDate));
    setCurrentPage(1);
  }, [tableData, selectedDate]);

  const sortedData = sortByDateTime(filteredTableData);
 
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="tradeVerifiedLinkTableBox">
        <div className="d-flex justify-content-between align-items-center verifiedTradingHeader">
          <div className="">
            <h2>Verified Trading Links</h2>
            <div className="verifiedPerformanceTitle align-items-center d-flex gap-4">
              <p>Blockchain Transaction ID's to prove your performance</p>
            </div>
          </div>

          <div className="text-end position-relative">
            <div className="SwithTitle d-flex flex-row">
              <CustomSwitch
                checked={isShowContent}
                onChange={() => setIsShowContent(!isShowContent)}
              />
              <span className="switchText">
                {isShowContent ? "Hide Details" : "Show Details"}
              </span>
            </div>
          </div>
        </div>
        {isShowContent && (
          <>
            <div className="row dateFilterWrapper">
              <div className="col-sm-6 col-12">
                <Link
                  to="#"
                  className="viewPublicPageLink"
                  onClick={() =>
                    navigate(`/verifiedTrades/${viewOnBlockchainParams}`)
                  }
                >
                  View public page {">"}
                </Link>
              </div>
              <div className="col-sm-6 col-12">
                <div className="verifiedLinkSearchBox d-flex justify-content-end mb-0">
                  <ExactDateSelector
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    minDate={user?.createdAt}
                    maxDate={dayjs()}
                  />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                {filteredTableData.length === 0 && selectedDate ? (
                  <div className="text-center py-4">
                    <p>
                      No results found for {selectedDate.format("DD-MM-YYYY")}
                    </p>
                  </div>
                ) : (
                  /* OLD: <GenericTable columns={columns} data={paginatedData} currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} noDataMessage="No data found" /> */
                  <div className="verifiedLinkTradesTableScope leaderVerifyTrade">
                    <VerifiedTradesTable
                      key={selectedDate ? selectedDate.toISOString() : "all"}
                      trades={sortedData}
                      columns={tradesTableColumns}
                      nameKey={null}
                      ordersKey="statsSnapshot.orders"
                      idKey="_id"
                      loading={false}
                      noDataMessage="No data found"
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </LocalizationProvider>
  );
};

export default TradeVerifiedLinkTable;
